import { COMMENT_REACTION_OBJECT_MODEL } from "@/hooks/useCommentReactions";
import { usePaginatedReactions } from "@/hooks/usePaginatedReactions";
import { reactionsApi } from "@/lib/humhub-api/reactions";
import type { ReactionType } from "@/lib/humhub-api/types";

interface UseReactionSummaryQueryOptions {
  contentId?: number | string | null;
  objectModel?: string;
  objectId?: number | string | null;
  enabled?: boolean;
  emotion?: ReactionType;
}

const REACTION_SUMMARY_PER_PAGE = 100;

export const useReactionSummaryQuery = ({
  contentId,
  objectModel,
  objectId,
  enabled = false,
  emotion,
}: UseReactionSummaryQueryOptions) => {
  const hasContentTarget = contentId != null && contentId !== "";
  const hasObjectTarget = Boolean(
    !hasContentTarget && objectModel && objectId != null && objectId !== "",
  );
  const shouldRender = hasContentTarget || hasObjectTarget;
  const resolvedObjectModel = objectModel ?? COMMENT_REACTION_OBJECT_MODEL;
  const queryKey = hasContentTarget
    ? (["reactions", contentId, REACTION_SUMMARY_PER_PAGE, emotion] as const)
    : ([
        "commentReactions",
        resolvedObjectModel,
        objectId,
        REACTION_SUMMARY_PER_PAGE,
        emotion,
      ] as const);

  const query = usePaginatedReactions({
    queryKey,
    enabled,
    hasTarget: shouldRender,
    missingTargetError: "Missing target for reaction summary query",
    fetchPage: async (page) => {
      if (hasContentTarget) {
        return await reactionsApi.getReactions(
          contentId as number | string,
          page,
          REACTION_SUMMARY_PER_PAGE,
          emotion,
        );
      }

      return await reactionsApi.findByObject(
        resolvedObjectModel,
        objectId as number | string,
        page,
        REACTION_SUMMARY_PER_PAGE,
        emotion,
      );
    },
  });

  return {
    hasContentTarget,
    hasObjectTarget,
    shouldRender,
    reactions: query.reactions ?? [],
    total: query.total ?? 0,
    isLoading: query.isLoading,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
